import { useState } from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { useToast } from "sj-ui-toolkit/src/hooks";

import type { Lesson, CustomResource } from "@/api/resources";
import {
  createResourceAsync,
  updateResourceAsync,
  deleteResourcesBulkAsync,
  loadResourcesForLessonAsync,
} from "@/api/resources";

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseResourcesOptions {
  selectedLesson: Lesson | null;
  setCustomResources: React.Dispatch<React.SetStateAction<CustomResource[]>>;
}

interface NewResource {
  name: string;
  description: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useResources = ({ selectedLesson, setCustomResources }: UseResourcesOptions) => {
  const { showToast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newResource, setNewResource] = useState<NewResource>({
    name: "",
    description: "",
    url: "",
  });
  const [selectedResources, setSelectedResources] = useState<CustomResource[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const reloadResources = async () => {
    if (!selectedLesson) return;
    const response = await loadResourcesForLessonAsync(String(selectedLesson.id));
    setCustomResources(response?.customResources ?? []);
  };

  const handleCreateResource = async () => {
    if (isCreating) return;
    if (!selectedLesson) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Please select a lesson first",
        life: 3000,
      });
      return;
    }
    if (!isValidUrl(newResource.url)) {
      showToast({
        severity: "error",
        summary: "Invalid URL",
        detail: "Please enter a valid URL (e.g., https://example.com)",
        life: 3000,
      });
      return;
    }

    try {
      setIsCreating(true);
      await createResourceAsync({ ...newResource, lesson_group_id: String(selectedLesson.id) });
      setShowCreateDialog(false);
      setNewResource({ name: "", description: "", url: "" });
      await reloadResources();
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Resource created successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error creating resource:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to create resource",
        life: 3000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleBulkDeleteResources = () => {
    if (!selectedResources.length) return;

    confirmDialog({
      message: `Are you sure you want to delete ${selectedResources.length} selected resource(s)?`,
      header: "Confirm Bulk Delete",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const idsToDelete = selectedResources.map(r => r.id);
          await deleteResourcesBulkAsync(idsToDelete);
          await reloadResources();
          setSelectedResources([]);
          showToast({
            severity: "success",
            summary: "Success",
            detail: `${selectedResources.length} resource(s) deleted successfully`,
            life: 3000,
          });
        } catch (error) {
          console.error("Error deleting resources:", error);
          showToast({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete some resources",
            life: 3000,
          });
        }
      },
    });
  };

  const onRowEditComplete = async (e: { newData: CustomResource; index: number }) => {
    const { newData } = e;

    if (!isValidUrl(newData.url)) {
      showToast({
        severity: "error",
        summary: "Invalid URL",
        detail: "Please enter a valid URL (e.g., https://example.com)",
        life: 3000,
      });
      await reloadResources();
      return;
    }

    try {
      await updateResourceAsync(newData.id, {
        name: newData.name,
        description: newData.description,
        url: newData.url,
      });
      await reloadResources();
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Resource updated successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error updating resource:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to update resource",
        life: 3000,
      });
    }
  };

  return {
    showCreateDialog,
    setShowCreateDialog,
    newResource,
    setNewResource,
    selectedResources,
    setSelectedResources,
    handleCreateResource,
    isCreating,
    handleBulkDeleteResources,
    onRowEditComplete,
  };
};
